// ============================================
// pages/storage/StorageDashboard.tsx — Storage Bills list (real API)
// Recurring monthly warehouse rent charged by CWC/TGSWC to MARKFED.
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Warehouse, Loader2, Plus, Edit2, CheckCircle, XCircle, ShieldCheck, Send, Download } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { seasonsAPI, commoditiesAPI, godownsAPI, storageBillsAPI } from '../../api/services';
import { UserRole, formatAmount, num } from '../../types/markfed';
import type { Season } from '../../types/markfed';

const STATUS_CONFIG: Record<string, { label: string; cls: string }> = {
  draft:            { label: 'Draft',            cls: 'bg-yellow-50 text-yellow-700 border-yellow-300' },
  submitted:        { label: 'Submitted',        cls: 'bg-blue-50 text-blue-700 border-blue-300' },
  dm_verified:      { label: 'DM Verified',      cls: 'bg-orange-50 text-orange-700 border-orange-300' },
  manager_approved: { label: 'Manager Approved', cls: 'bg-green-50 text-green-700 border-green-300' },
  rejected:         { label: 'Rejected',         cls: 'bg-red-50 text-red-700 border-red-300' },
};
const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const csvEscape = (v: any): string => {
  if (v == null) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const exportCsv = (bills: any[], seasonName: string) => {
  const headers = [
    'Bill No', 'Bill Date', 'Month', 'Year', 'Godown', 'Commodity',
    'Bags', 'Days', 'Rate/bag/mo', 'Gross', 'Discount', 'CGST', 'SGST', 'Net', 'Total Payable', 'Balance', 'Status',
  ];
  const rows = bills.map((b) => [
    b.bill_no || '',
    b.bill_date || '',
    b.bill_month ? MONTHS[b.bill_month] : '',
    b.bill_year || '',
    b.godown?.godown_name || '',
    b.commodity?.name || '',
    b.num_bags ?? '',
    b.num_days ?? '',
    b.rate_per_bag_per_month ?? '',
    b.gross_amount ?? '',
    b.discount_amount ?? '',
    b.cgst_amount ?? '',
    b.sgst_amount ?? '',
    b.net_amount ?? '',
    b.total_payable ?? '',
    b.balance_to_release ?? '',
    b.status || 'draft',
  ]);
  const csv = [headers, ...rows].map((r) => r.map(csvEscape).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `storage-bills-${seasonName.replace(/\s+/g, '_')}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

export const StorageDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canSubmit = hasRole(UserRole.DM, UserRole.AO_CAO, UserRole.SUPER_ADMIN);
  const canVerify = hasRole(UserRole.DM, UserRole.SUPER_ADMIN);
  const canApprove = hasRole(UserRole.MD, UserRole.SUPER_ADMIN);

  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<number>(0);
  const [commodities, setCommodities] = useState<any[]>([]);
  const [godowns, setGodowns] = useState<any[]>([]);
  const [bills, setBills] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [commodityFilter, setCommodityFilter] = useState<number>(0);
  const [godownFilter, setGodownFilter] = useState<number>(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, c, g] = await Promise.all([seasonsAPI.list(), commoditiesAPI.list(), godownsAPI.list()]);
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        setCommodities(c.data);
        setGodowns(g.data);
        const active = list.find((x) => x.is_active) || list[0];
        if (active) setSelectedSeasonId(active.id);
      } catch (err: any) {
        setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to load' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedSeasonId) return;
    (async () => {
      setLoading(true);
      try {
        const params: any = {};
        if (commodityFilter) params.commodity_id = commodityFilter;
        if (godownFilter) params.godown_id = godownFilter;
        const res = await storageBillsAPI.list(selectedSeasonId, params);
        setBills(Array.isArray(res.data) ? res.data : []);
      } catch (err: any) {
        setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to load bills' });
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedSeasonId, commodityFilter, godownFilter]);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return bills;
    return bills.filter((b) => (b.status || 'draft') === statusFilter);
  }, [bills, statusFilter]);

  const advance = async (id: number, fn: () => Promise<any>, nextStatus: string, msg: string) => {
    setActionLoading(id);
    setMessage(null);
    try {
      await fn();
      setBills((prev) => prev.map((b) => (b.id === id ? { ...b, status: nextStatus } : b)));
      setMessage({ type: 'success', text: msg });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || `Failed: ${msg}` });
    } finally {
      setActionLoading(null);
    }
  };

  const doSubmit = (id: number) => window.confirm('Submit for DM verification?')
    && advance(id, () => storageBillsAPI.submit(selectedSeasonId, id), 'submitted', 'Submitted');
  const doVerify = (id: number) => window.confirm('Verify as DM?')
    && advance(id, () => storageBillsAPI.verify(selectedSeasonId, id), 'dm_verified', 'DM verified');
  const doApprove = (id: number) => window.confirm('Approve as Manager?')
    && advance(id, () => storageBillsAPI.approve(selectedSeasonId, id), 'manager_approved', 'Approved');
  const doReject = async (id: number) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason?.trim()) return;
    await advance(id, () => storageBillsAPI.reject(selectedSeasonId, id, reason.trim()), 'rejected', 'Rejected');
  };

  if (loading && bills.length === 0) {
    return <div className="max-w-7xl mx-auto flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-blue-600 animate-spin" /><span className="ml-2 text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Warehouse className="w-7 h-7 text-indigo-600" />
            Storage Bills
          </h1>
          <p className="text-sm text-gray-500 mt-1">Monthly warehouse storage charges from CWC/TGSWC/AMC</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => exportCsv(filtered, seasons.find((s) => s.id === selectedSeasonId)?.season_name || 'season')}
            disabled={filtered.length === 0}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed">
            <Download className="w-4 h-4" />Export CSV
          </button>
          <button onClick={() => navigate('/storage/bills/create')}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg text-sm">
            <Plus className="w-4 h-4" />Add Storage Bill
          </button>
        </div>
      </div>

      <div className="mb-4 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Season</label>
            <select value={selectedSeasonId} onChange={(e) => setSelectedSeasonId(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              {seasons.map((s) => (<option key={s.id} value={s.id}>{s.season_name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Commodity</label>
            <select value={commodityFilter} onChange={(e) => setCommodityFilter(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value={0}>All</option>
              {commodities.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Godown</label>
            <select value={godownFilter} onChange={(e) => setGodownFilter(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value={0}>All</option>
              {godowns.map((g) => (<option key={g.id} value={g.id}>{g.godown_name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="all">All</option>
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="dm_verified">DM Verified</option>
              <option value="manager_approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {message && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">#</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Bill No</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Month/Year</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Godown</th>
                <th className="px-3 py-3 text-left font-semibold text-gray-600 uppercase">Commodity</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Bags</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Days</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Net</th>
                <th className="px-3 py-3 text-right font-semibold text-gray-600 uppercase">Payable</th>
                <th className="px-3 py-3 text-center font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-3 py-3 text-center font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-12 text-center text-gray-400">No storage bills. Click "Add Storage Bill".</td></tr>
              ) : filtered.map((b, i) => {
                const st = b.status || 'draft';
                const cfg = STATUS_CONFIG[st] || { label: st, cls: 'bg-gray-100 text-gray-500 border-gray-300' };
                const isLoading = actionLoading === b.id;
                return (
                  <tr key={b.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-500">{i + 1}</td>
                    <td className="px-3 py-2 font-medium text-gray-900">{b.bill_no || '--'}</td>
                    <td className="px-3 py-2 text-gray-600">{MONTHS[b.bill_month || 0]} {b.bill_year || ''}</td>
                    <td className="px-3 py-2 text-gray-700">{b.godown?.godown_name || '--'}</td>
                    <td className="px-3 py-2 text-gray-700">{b.commodity?.name || '--'}</td>
                    <td className="px-3 py-2 text-right">{num(b.num_bags).toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-right">{num(b.num_days)}</td>
                    <td className="px-3 py-2 text-right font-medium">{formatAmount(num(b.net_amount))}</td>
                    <td className="px-3 py-2 text-right font-bold text-indigo-700">{formatAmount(num(b.total_payable))}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cfg.cls}`}>{cfg.label}</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-1 flex-wrap">
                        {(st === 'draft' || st === 'rejected') && (
                          <button onClick={() => navigate(`/storage/bills/edit/${b.id}`)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded">
                            <Edit2 className="w-3 h-3" />Edit
                          </button>
                        )}
                        {st === 'draft' && canSubmit && (
                          <button onClick={() => doSubmit(b.id)} disabled={isLoading}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50">
                            {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}Submit
                          </button>
                        )}
                        {st === 'submitted' && canVerify && (
                          <button onClick={() => doVerify(b.id)} disabled={isLoading}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded disabled:opacity-50">
                            <ShieldCheck className="w-3 h-3" />Verify
                          </button>
                        )}
                        {st === 'dm_verified' && canApprove && (
                          <button onClick={() => doApprove(b.id)} disabled={isLoading}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-white bg-green-600 hover:bg-green-700 rounded disabled:opacity-50">
                            <CheckCircle className="w-3 h-3" />Approve
                          </button>
                        )}
                        {['submitted', 'dm_verified'].includes(st) && (canVerify || canApprove) && (
                          <button onClick={() => doReject(b.id)} disabled={isLoading}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50">
                            <XCircle className="w-3 h-3" />Reject
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StorageDashboard;
